import { UNKNOWN_DATUM, YEAR_COLUMN_KEY } from 'business/constants';
import { isDate } from 'helpers/helpers';
import { camelize } from 'helpers/syntaxHelper';
import { getYearlyComparisonScale } from 'helpers/yearlyComparison/scale';
import { getYearlyComparisonSettingsForScale } from 'helpers/yearlyComparison/settings';
import i18n from 'i18n';
import _ from 'lodash';
import { UnformatedChart, UnformatedVizData } from './types';

export const METRIC_KEY = 'metric';
const VALUE_KEY = 'value';
/**
 * Pivot data so that multimetrics can be displayed on a chart.
 * We add a dimension which is a metric name, and we remove all the metric columns.
 * [{ firstMetric: 10, secondMetric: 20, dimension: 'A' }] will return
 * [
 *     {metric: 'firstMetric', dimension: 'A', value: 1},
 *     {metric: 'secondMetric', dimension: 'A', value: 2},
 * ]
 */
export function pivotMultimetrics(
    data: ResponseDataT[],
    columns: ResponseColumnT[],
    metrics: QueryMetricT[],
): UnformatedChart {
    // TODO: improve typing
    const chartData: ResponseDataT[] = [];
    const metricsKeys = metrics.map((m) => camelize(m.field));
    const chartColumns: ResponseColumnT[] = columns.filter(
        (col) => !col.isMetric,
    );

    data.forEach((d) => {
        const newD = { ...d };
        metricsKeys.forEach((key) => {
            delete newD[key];
        });
        // We add one data point for each metric
        metricsKeys.forEach((key) => {
            // @ts-expect-error chartData filter is meesing with ts
            chartData.push({
                ...newD,
                metric: key,
                value: d[key],
            });
        });
    });

    let valueType = 'float';
    if (
        columns
            .filter((col) => metricsKeys.includes(col.key))
            .every((c) => c.type === columns[0].type)
    ) {
        // The type is always the same, we can apply it
        valueType = columns[0].type;
    }

    // We add the global value

    chartColumns.push({
        key: VALUE_KEY,
        label: i18n.t('common.amount'),
        // @ts-expect-error [TS migration] Was not detected by flow
        type: valueType,
        isMetric: true,
    });
    // And the categorical dimension used to display the metrics

    chartColumns.push({
        key: METRIC_KEY,
        label: i18n.t('common.type'),
        type: 'category',
    });

    return { chartData, chartColumns };
}

export const pivotMultimetricsWithContext = (
    context: UnformatedVizData,
): UnformatedVizData => {
    const {
        chartData,
        chartColumns,
        context: { queryMetrics },
    } = context;
    if (queryMetrics.length > 1) {
        const pivotedData = pivotMultimetrics(
            chartData,
            chartColumns,
            queryMetrics,
        );
        return {
            ...context,
            chartData: pivotedData.chartData,
            chartColumns: pivotedData.chartColumns,
        };
    }
    return context;
};

/**
 * Add two columns, one for year and the other for month, and remove the date dimension
 * on which the comparison is based. We determine the date column automatically.
 * Only works when there is a month-aggregated date column.
 * [{ firstMetric: 10, secondMetric: 20, dimension: 'B', date: new Date('2017-01-01').getTime()}]
 * -> [{ firstMetric: 10, secondMetric: 20, dimension: 'B', year: 2017, month: 'Jan'}]
 */
export function pivotYearlyComparison(
    data: ResponseDataT[],
    columns: ResponseColumnT[],
): FlowAnyObject {
    // TODO: improve typing
    // We pivot on the first date column with the right scale
    const scale = getYearlyComparisonScale(columns);
    const dateColumn = columns.find(
        (col) => isDate(col.type) && col.scale === scale,
    );
    if (!dateColumn || !scale) {
        return { chartData: data, tableColumns: columns };
    }
    const yearlyComparisonSettings = getYearlyComparisonSettingsForScale(scale);
    const dateKey = dateColumn.key;
    // For each data point, we add the year and the month
    const chartData: ResponseDataT[] = _(data)
        .filter((d) => d[dateKey] !== UNKNOWN_DATUM)
        .map((d) => {
            const pointValue = d[dateKey];
            if (
                typeof pointValue !== 'number' &&
                typeof pointValue !== 'string' &&
                !(pointValue instanceof Date)
            ) {
                throw new Error(
                    `Unexpected type received by pivotYearlyComparison: ${typeof pointValue}`,
                );
            }
            const date = new Date(pointValue);
            const newD = { ...d };
            delete newD[dateKey];
            return {
                ...newD,
                year: date.getFullYear(),
                [scale]: yearlyComparisonSettings.parseValue(date),
            };
        })
        .sortBy(['year'])
        .value();

    // We add the year and the month as columns
    const tableColumns: ResponseColumnT[] = columns.filter(
        (col) => col.key !== dateKey,
    );

    tableColumns.push({
        key: YEAR_COLUMN_KEY,
        label: i18n.t('common.year'),
        type: 'year',
    });

    tableColumns.push({
        key: scale,
        label: i18n.t(`common.${scale}`),
        type: 'category',
    });

    return { chartData, tableColumns };
}
